import { useMemo } from 'react';
import { Field, FieldTypes } from '@common-fe/common-fe';
import * as yup from 'yup';

import {FieldStateKey, NON_ASSIGNABLE_VALUE,PermissionKey,REQUIRED_TEXT } from '@/common/constants';
import { useHideFields } from '@/modules/employer/components/SetupEmployer/hooks';
import { useSetupEmployerMode } from '@/modules/employer/components/SetupEmployer/hooks/useSetupEmployerMode';
import { BankInfo } from '@/modules/employer/components/SetupEmployer/SetupEmployer.types';
import { EmployerDto } from '@/modules/employer/types';
import useGetPermissionToEditOverridableField from '@/modules/user/hooks/useGetPermissionToEditOverridableField';

import { AccountUsageValues } from '../AccountUsage.types';

import useAdminFeesValidation from './useAdminFeesValidation';

const placeholder = 'Select bank account';

const useAdministratorFeesFields = (
  banks: BankInfo[],
  formattedData: EmployerDto,
  currentValues?: AccountUsageValues,
  activateMode?: boolean,
  isLoading?: boolean,
) => {
  const { isValidThroughEmployeeFee } = useAdminFeesValidation();
  const { viewMode } = useSetupEmployerMode();

  const defaultValue = useMemo(() => {
    const externalAdministratorFeesAccount = formattedData
      ?.normalProperties?.externalAdministratorFeesAccount;
    const bankId = banks.find((bank) => bank.id === externalAdministratorFeesAccount)?.id;

    if (bankId) return bankId;

    if (formattedData?.normalProperties?.externalAccountEnable
      || externalAdministratorFeesAccount === NON_ASSIGNABLE_VALUE) return NON_ASSIGNABLE_VALUE;

    return '';
  }, [banks, formattedData]);

  const externalAdministratorFees = useMemo(
    () => currentValues?.administratorFeesBank,
    [currentValues],
  );

  const externalAdministratorFeesAccountProps = useGetPermissionToEditOverridableField(
    PermissionKey.EXTERNAL_ADMINISTRATOR_FEES_ACCOUNT,
    FieldStateKey.EXTERNAL_ADMINISTRATOR_FEES_ACCOUNT,
  );
  const fields =  useMemo(() => {
    if (isLoading) return [];

    const options = banks.map((bank) => ({
      key: bank.id,
      value: bank.description,
    }));
    
    const formattedFields = [
      {
        name: 'administratorFeesBank',
        type: FieldTypes.Dropdown,
        disabled: viewMode,
        validator: activateMode
          ? yup.string().test({
            test: isValidThroughEmployeeFee,
            message: REQUIRED_TEXT,
          }).required(REQUIRED_TEXT)
          : yup.string(),
        placeholder,
        label: 'Bank Account',
        singleMode: true,
        areSubFieldsShouldHide: !externalAdministratorFees || externalAdministratorFees === NON_ASSIGNABLE_VALUE,
        options,
        showRequireIcon: true,
        defaultValue: defaultValue,
        value: defaultValue,
        hasHorizontalLine: true,
        ...externalAdministratorFeesAccountProps,
      },
    ] as Field[];
    return formattedFields;
  }, [
    externalAdministratorFeesAccountProps,
    isValidThroughEmployeeFee,
    activateMode,
    banks,
    externalAdministratorFees,
    defaultValue,
    isLoading,
    viewMode,
  ]);

  const formattedFields = useHideFields(fields);
  return formattedFields;
};

export default useAdministratorFeesFields;
